import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Loader2, LogIn, Receipt, Wallet } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatRupiah } from "../lib/format";
import { CountUp } from "../components/Common";
import { TypeBadge, tdClass, thClass } from "../components/Common";

const REFRESH_SECONDS = 60;
const BULAN_SINGKAT = ["JAN", "FEB", "MAR", "APR", "MEI", "JUN", "JUL", "AGU", "SEP", "OKT", "NOV", "DES"];
const fmtDisplay = (d) =>
  `${String(d.getDate()).padStart(2, "0")} ${BULAN_SINGKAT[d.getMonth()]} ${d.getFullYear()} · ${d.toLocaleTimeString("id-ID", { hour12: false })} WIB`;

export default function DisplayDashboard() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [books, setBooks] = useState(null);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % 2), 15000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(() => {
    api.get("/stats/books")
      .then((b) => {
        setBooks(b.data);
        const book = (b.data?.resmi_count ?? 0) > 0 ? "resmi" : "imf";
        return api.get(`/stats/dashboard?book=${book}`);
      })
      .then((d) => {
        setData(d.data);
        setError(false);
        setUpdatedAt(new Date());
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      setTick((x) => x + 1);
      load();
    }, REFRESH_SECONDS * 1000);
    return () => clearInterval(t);
  }, [load]);

  const tickerText = data
    ? `IMF-831 · SISTEM LAPORAN KAS & KEUANGAN · SALDO KAS RESMI ${formatRupiah(books?.resmi ?? data.official_balance)} · SALDO IMF ${formatRupiah(books?.imf ?? data.balance)} · ${data.count} TRANSAKSI TERCATAT · AKUNTABEL · TRANSPARAN · TEPAT WAKTU`
    : "IMF-831 · SISTEM LAPORAN KAS & KEUANGAN";

  if (error)
    return (
      <div className="min-h-screen bg-[#070E1B] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <img src="/assets/logo-imf.png" alt="IMF-831" className="w-12 h-12 sm:w-14 sm:h-14 object-contain" />
        <p className="text-slate-400 text-sm">Mode Display membutuhkan login.</p>
        <button
          data-testid="btn-display-login"
          onClick={() => navigate("/login")}
          className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 shadow-lg shadow-blue-500/25"
        >
          <LogIn className="w-4 h-4" /> Masuk untuk Melihat Dashboard
        </button>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-[#070E1B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#070E1B] text-slate-100 p-5 lg:p-8 relative overflow-hidden" data-testid="display-dashboard">
      <img
        src="/assets/logo-imf.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute -right-10 -bottom-10 w-48 sm:w-72 lg:-right-16 lg:-bottom-16 lg:w-80 opacity-[0.05]"
      />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img src="/assets/logo-imf.png" alt="IMF-831" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
          <div>
            <p className="font-extrabold tracking-tight leading-tight">IMF-831</p>
            <p className="text-[11px] text-slate-400">Dashboard Kas — Mode Display</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <p data-testid="display-clock" className="font-num text-sm font-bold tracking-widest text-cyan-400">{fmtDisplay(now)}</p>
          <div className="flex items-center gap-2" data-testid="display-status">
            <span className="led-dot" />
            <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase hidden sm:inline">Sistem Aktif</span>
          </div>
          <p className="text-[11px] text-slate-400 hidden md:block">
            {updatedAt ? `Diperbarui ${updatedAt.toLocaleTimeString("id-ID")}` : ""} · muat ulang otomatis {REFRESH_SECONDS} dtk
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6 fade-up" style={{ animationDelay: "0.15s" }}>
        {hasPerm("view_saldo_resmi") && (
        <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-5" data-testid="display-saldo-resmi">
          <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Saldo Kas Resmi
          </p>
          <p className="font-num text-xl lg:text-2xl font-bold text-cyan-400 mt-2"><CountUp value={books?.resmi ?? data.official_balance} format={formatRupiah} /></p>
        </div>
        )}
        {hasPerm("view_saldo_imf") && (
        <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-5" data-testid="display-saldo-imf">
          <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Saldo IMF
          </p>
          <p className="font-num text-2xl lg:text-3xl font-bold text-emerald-400 mt-2"><CountUp value={books?.imf ?? data.balance} format={formatRupiah} /></p>
        </div>
        )}
        <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-5" data-testid="display-pemasukan">
          <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-1.5">
            <ArrowDownRight className="w-3.5 h-3.5" /> Total Pemasukan
          </p>
          <p className="font-num text-xl lg:text-2xl font-bold text-emerald-400 mt-2"><CountUp value={data.total_income} format={formatRupiah} /></p>
        </div>
        <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-5" data-testid="display-pengeluaran">
          <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-1.5">
            <ArrowUpRight className="w-3.5 h-3.5" /> Total Pengeluaran
          </p>
          <p className="font-num text-xl lg:text-2xl font-bold text-rose-400 mt-2"><CountUp value={data.total_expense} format={formatRupiah} /></p>
        </div>
        <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-5" data-testid="display-count">
          <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-1.5">
            <Receipt className="w-3.5 h-3.5" /> Jumlah Transaksi
          </p>
          <p className="font-num text-xl lg:text-2xl font-bold text-slate-100 mt-2">{data.count}</p>
        </div>
      </div>

      <div className="grid fade-up" style={{ animationDelay: "0.25s" }}>
      <div className={`col-start-1 row-start-1 transition-opacity duration-700 ${slide === 0 ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-3 rounded-xl bg-[#1E293B] border border-slate-800 p-5">
          <h3 className="text-sm font-bold text-slate-100 mb-4">Pemasukan vs Pengeluaran per Bulan</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8 }} labelStyle={{ color: "#F8FAFC" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Pemasukan" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Pengeluaran" fill="#F43F5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="xl:col-span-2 rounded-xl bg-[#1E293B] border border-slate-800 overflow-hidden">
          <h3 className="text-sm font-bold text-slate-100 px-5 pt-5 pb-3">Transaksi Terbaru</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-left">
              <thead>
                <tr>
                  <th className="bg-[#0F172A] text-slate-400 font-semibold uppercase text-[10px] tracking-wider p-3 border-b border-slate-800">Tanggal</th>
                  <th className="bg-[#0F172A] text-slate-400 font-semibold uppercase text-[10px] tracking-wider p-3 border-b border-slate-800">Uraian</th>
                  <th className="bg-[#0F172A] text-slate-400 font-semibold uppercase text-[10px] tracking-wider p-3 border-b border-slate-800 text-right">Nominal</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/60">
                    <td className="p-3 text-xs text-slate-400 whitespace-nowrap">{formatDateID(t.date)}</td>
                    <td className="p-3 text-slate-100 max-w-[180px]"><span className="line-clamp-1">{t.description}</span></td>
                    <td className={`p-3 text-right font-num font-semibold text-xs ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                      {t.type === "income" ? "+" : "−"}{formatRupiah(t.amount)}
                    </td>
                  </tr>
                ))}
                {data.recent.length === 0 && (
                  <tr><td colSpan={3} className="p-8 text-center text-slate-400 text-sm">Belum ada transaksi</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
      <div className={`col-start-1 row-start-1 transition-opacity duration-700 flex flex-col justify-center ${slide === 1 ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid="display-slide-saldo">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {hasPerm("view_saldo_resmi") && (
          <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-8 text-center">
            <p className="text-xs font-bold tracking-[0.3em] text-slate-400 uppercase">Saldo Kas Resmi</p>
            <p className="font-num text-4xl lg:text-6xl font-bold text-cyan-400 mt-4"><CountUp value={books?.resmi ?? data.official_balance} format={formatRupiah} duration={1200} /></p>
          </div>
        )}
        {hasPerm("view_saldo_imf") && (
          <div className="rounded-xl bg-[#1E293B] border border-slate-800 p-8 text-center">
            <p className="text-xs font-bold tracking-[0.3em] text-slate-400 uppercase">Saldo IMF</p>
            <p className="font-num text-4xl lg:text-6xl font-bold text-emerald-400 mt-4"><CountUp value={books?.imf ?? data.balance} format={formatRupiah} duration={1200} /></p>
          </div>
        )}
      </div>
      </div>
      </div>

      <div className="flex justify-center gap-2 mt-6 mb-12">
        {[0, 1].map((i) => (
          <button
            key={i}
            data-testid={`display-slide-dot-${i}`}
            onClick={() => setSlide(i)}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${slide === i ? "bg-blue-600" : "bg-slate-600"}`}
          />
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 h-9 bg-[#070E1B] border-t border-slate-800 overflow-hidden flex items-center" data-testid="display-ticker">
        <div className="ticker-track font-num text-xs tracking-[0.2em] text-cyan-400 uppercase">
          {tickerText}&nbsp;&nbsp;•&nbsp;&nbsp;{tickerText}
        </div>
      </div>
    </div>
  );
}
